import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {I18nLang, LangService} from 'ngx-satoris';
import {environment} from 'src/environments/environment';
import {ShowcaseConfig} from './configs/showcase-config.interface';
import {SHOWCASE_CONFIG} from './configs/showcase.config';
import {ApiService} from 'src/app/shared/services/api.service';

declare const window: any;

export interface ShowcaseStep {
  num: string;
  label: string;
  paneTitle: string;
  paneDesc: string;
}

@Component({
  selector: 'app-showcase',
  templateUrl: './showcase.component.html',
  styleUrls: ['./showcase.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ShowcaseComponent implements AfterViewInit, OnDestroy, OnInit {
  public isEvisaMode = SHOWCASE_CONFIG.showcase.showEvisa;
  public showPassport = SHOWCASE_CONFIG.showcase.showPassport;
  public showIdCard = SHOWCASE_CONFIG.showcase.showIdCard;
  public showResidencePermit = SHOWCASE_CONFIG.showcase.showResidencePermit;
  public showEvisa = SHOWCASE_CONFIG.showcase.showEvisa;
  private cleanups: (() => void)[] = [];

  atBottom = false;
  showLangMenu = false;
  activePaneIndex = 0;
  currentLang: number | null = null;
  config: ShowcaseConfig = SHOWCASE_CONFIG;
  languageOptions: {value: number; label: string}[] = [];

  get currentLangLabel(): string {
    const opt = this.languageOptions.find(o => o.value === this.currentLang);
    return opt ? opt.label.split('.').pop()?.toUpperCase() ?? '' : '';
  }

  get isArabic(): boolean {
    return this.currentLang === I18nLang.AR;
  }

  get heroSubtitleKey(): string {
    if(this.showEvisa) return 'showcase.heroSubtitle.evisa';
    const parts: string[] = [];
    if(this.showPassport) parts.push('passport');
    if(this.showIdCard) parts.push('idCard');
    if(this.showResidencePermit) parts.push('residencePermit');
    if(!parts.length) return 'showcase.heroSubtitle';
    return `showcase.heroSubtitle.${parts.join('_')}`;
  }

  constructor(private host: ElementRef<HTMLElement>, private router: Router, public api: ApiService, private cdr: ChangeDetectorRef, private lang: LangService) {}

  ngOnInit(): void {
    this.loadCountryConfig();
    this.initializeLanguageOptions();
  }

  private loadCountryConfig(): void {
    this.config = SHOWCASE_CONFIG;
  }

  private initializeLanguageOptions(): void {
    const supportedLanguages = this.api.env.supportedLanguages;
    const systemLangs = (I18nLang as unknown) as Record<string, number>;
    this.languageOptions = Object.keys(systemLangs)
      .filter(key => isNaN(Number(key)))
      .map(key => ({label: 'form.lang.' + key, value: systemLangs[key] as unknown as number}))
      .filter(opt => supportedLanguages.includes(opt.value));

    const storedLanguage = localStorage.getItem('lang');
    const navigatorLanguage = navigator.language || (navigator as any).userLanguage;
    let defaultLanguage = storedLanguage ? +storedLanguage : I18nLang[(navigatorLanguage || 'en').split('-')[0].toUpperCase() as keyof typeof I18nLang];
    if(!supportedLanguages.includes(defaultLanguage)) {
      defaultLanguage = supportedLanguages[0];
    }
    this.currentLang = defaultLanguage;
  }

  onChangeLang(value: string): void {
    const langValue = Number(value) as unknown as I18nLang;
    this.lang.setLang(langValue);
    localStorage.setItem('lang', String(langValue));
    try {
      const code = this.lang.langType(langValue).toLowerCase();
      window.updateGlpi(code, undefined, environment.faqSource, environment.appVersion);
    } catch { /* empty */ }
    this.currentLang = Number(langValue);
  }

  selectLang(value: number): void {
    this.onChangeLang(String(value));
    this.showLangMenu = false;
  }

  get showcaseDocumentList(): string {
    const docs: string[] = [];
    if(this.showPassport) docs.push(this.lang.transform('DYN.document.P'));
    if(this.showIdCard) docs.push(this.lang.transform('DYN.document.ID'));
    if(this.showResidencePermit) docs.push(this.lang.transform('DYN.document.IR'));
    if(this.showEvisa) docs.push(this.lang.transform('showcase.portalType'));
    return docs.join(' · ');
  }

  readonly flowSteps: ShowcaseStep[] = [
    {num: '01', label: 'showcase.step1.label', paneTitle: 'showcase.step1.paneTitle', paneDesc: 'showcase.step1.paneDesc'},
    {num: '02', label: 'showcase.step2.label', paneTitle: 'showcase.step2.paneTitle', paneDesc: 'showcase.step2.paneDesc'},
    {num: '03', label: 'showcase.step3.label', paneTitle: 'showcase.step3.paneTitle', paneDesc: 'showcase.step3.paneDesc'},
    {num: '04', label: 'showcase.step4.label', paneTitle: 'showcase.step4.paneTitle', paneDesc: 'showcase.step4.paneDesc'}
  ];


  readonly stepImages = [
    'assets/showcase/step1.svg',
    'assets/showcase/step2.svg',
    'assets/showcase/step3.svg',
    'assets/showcase/step4.svg'
  ];

  get holoStrip(): string {
    const n = this.api.env.name.toUpperCase();
    return `★ ${n} ★ ${n} ★ ${n} ★ ${n} ★`;
  }

  private monthShort(month: number): string {
    const keys = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    const name = this.lang.transform('month.' + keys[month - 1]);
    return this.isArabic ? name : name.slice(0, 3).toUpperCase();
  }

  get heroCards() {
    const country = this.api.env.name.toUpperCase();
    const countryName = this.api.env.name.replace(/\s*(evisa|eguichet|portal|visa)\s*$/i, '').trim().toUpperCase();
    const code = this.api.env.name.slice(0, 3).toUpperCase();
    const jan = this.monthShort(1);
    const apr = this.monthShort(4);
    const may = this.monthShort(5);
    const jun = this.monthShort(6);
    return {
      passport: {
        surname: 'SPECIMEN',
        givenNames: 'SARAH',
        nationality: this.api.env.nationalityNoDeclaration,
        dob: `01 ${jan} 2000`,
        sex: 'F',
        placeOfBirth: countryName,
        issueDate: `01 ${jan} 2025`,
        expiryDate: `01 ${jan} 2035`,
        authority: 'DIR. PASSEPORTS · ' + countryName,
        number: code + '•••••',
        mrz1: 'P<' + code + 'SPECIMEN<<SARAH<<<<<<<<<<<<<<',
        mrz2: code + '•••••<5' + code + '0001011F3501011<<<<<4'
      },
      idCard: {
        surname: 'SPECIMEN',
        given: 'SARAH',
        dob: '01 / 01 / 2000',
        sex: 'F',
        number: code + ' · 000001',
        mrz1: 'ID' + code + '000001<SPECIMEN<<SARAH<<<<<<<<',
        mrz2: '0001011F3501011' + code + '<<<<<<<<<<<6'
      },
      permit: {
        holder: 'SPECIMEN, SARAH',
        category: 'B — LONG STAY · 5Y',
        valid: '2025-01 → 2030-01',
        number: 'RP-00001'
      },
      evisa: {
        surname: 'SPECIMEN',
        givenNames: 'SARAH',
        nationality: countryName,
        dob: `02 ${apr} 2000`,
        number: code + '·V·00001',
        validFrom: `20 ${may} 2026`,
        validUntil: `19 ${jun} 2026`,
        portOfEntry: 'ALL AUTHORIZED PORTS',
        issuer: country,
        issuedAt: `20 ${may} 2026 · 14:32 UTC`
      }
    };
  }

  setActiveStep(i: number) {
    this.activePaneIndex = i;
    this.cdr.detectChanges();
  }

  go(route: string) {
    this.router.navigate([route]);
  }

  scrollToBottom() {
    const page = this.host.nativeElement.querySelector<HTMLElement>('.vsc-page');
    page?.scrollTo({top: page.scrollHeight, behavior: 'smooth'});
  }

  ngAfterViewInit() {
    this.initScrollChoreography();
  }

  ngOnDestroy() {
    this.cleanups.forEach(fn => fn());
  }

  private initScrollChoreography() {
    const page = this.host.nativeElement.querySelector<HTMLElement>('.vsc-page')!;
    const $$ = (q: string, c: Element | Document = page) => Array.from(c.querySelectorAll<HTMLElement>(q));
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sy = () => page.scrollTop;
    const vh = () => page.clientHeight;

    /* Reveal on scroll */
    const revealObs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if(e.isIntersecting) {
          (e.target as HTMLElement).classList.add('in');
          revealObs.unobserve(e.target);
        }
      });
    }, {root: page, threshold: 0.12, rootMargin: '0px 0px -8% 0px'});
    $$('.reveal').forEach(el => revealObs.observe(el));
    this.cleanups.push(() => revealObs.disconnect());

    /* Counter animation on dash cards */
    const animateCounter = (el: HTMLElement) => {
      const to = +(el as HTMLElement & {dataset: {to: string}}).dataset.to;
      const dur = 1600;
      const t0 = performance.now();
      const tick = (t: number) => {
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(to * eased).toLocaleString();
        if(p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const inObs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if(e.isIntersecting) {
          (e.target as HTMLElement).classList.add('in');
          const card = e.target as HTMLElement & {dataset: {counted?: string}};
          if(card.dataset.counted !== '1' && card.querySelector('.counter')) {
            card.dataset.counted = '1';
            card.querySelectorAll<HTMLElement>('.counter').forEach(animateCounter);
          }
          inObs.unobserve(e.target);
        }
      });
    }, {root: page, threshold: 0.25});
    $$('.dash-card').forEach(el => inObs.observe(el));
    this.cleanups.push(() => inObs.disconnect());

    /* Parallax */
    const parallaxEls = $$('[data-parallax]').map(el => ({
      el,
      speed: parseFloat((el as HTMLElement & {dataset: {parallax: string}}).dataset.parallax),
      baseTop: el.getBoundingClientRect().top + sy()
    }));
    let raf: number | null = null;
    const updateParallax = () => {
      const s = sy(), v = vh();
      parallaxEls.forEach(({el, speed, baseTop}) => {
        el.style.transform = `translate3d(0, ${(s + v / 2 - baseTop) * speed}px, 0)`;
      });
      const orbA = page.querySelector<HTMLElement>('.orb-a');
      const orbB = page.querySelector<HTMLElement>('.orb-b');
      const orbC = page.querySelector<HTMLElement>('.orb-c');
      if(orbA) orbA.style.transform = `translate3d(0, ${s * -0.05}px, 0)`;
      if(orbB) orbB.style.transform = `translate3d(0, ${s * -0.1}px, 0)`;
      if(orbC) orbC.style.transform = `translate3d(0, ${s * -0.07}px, 0)`;
      raf = null;
    };
    const onScroll = () => {
      if(!raf) raf = requestAnimationFrame(updateParallax);
      const bottom = page.scrollTop + page.clientHeight >= page.scrollHeight - 32;
      if(bottom !== this.atBottom) {
        this.atBottom = bottom;
        this.cdr.detectChanges();
      }
    };
    page.addEventListener('scroll', onScroll, {passive: true});
    updateParallax();
    this.cleanups.push(() => page.removeEventListener('scroll', onScroll));

    /* Magnetic buttons */
    $$('.magnetic').forEach(btn => {
      let rx = 0, ry = 0, tx = 0, ty = 0, raf2: number | null = null;
      const tick = () => {
        tx += (rx - tx) * 0.18; ty += (ry - ty) * 0.18;
        btn.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
        if(Math.abs(rx - tx) > 0.1 || Math.abs(ry - ty) > 0.1) raf2 = requestAnimationFrame(tick);
        else raf2 = null;
      };
      btn.addEventListener('mousemove', (e: Event) => {
        const me = e as MouseEvent;
        const r = btn.getBoundingClientRect();
        rx = (me.clientX - (r.left + r.width / 2)) * 0.18;
        ry = (me.clientY - (r.top + r.height / 2)) * 0.28;
        if(!raf2) raf2 = requestAnimationFrame(tick);
      });
      btn.addEventListener('mouseleave', () => {
        rx = 0; ry = 0; if(!raf2) raf2 = requestAnimationFrame(tick);
      });
    });

    /* Card tilt */
    $$('[data-tilt]').forEach(card => {
      let raf3: number | null = null;
      let target = {rx: 0, ry: 0};
      const current = {rx: 0, ry: 0};
      const tick = () => {
        current.rx += (target.rx - current.rx) * 0.12;
        current.ry += (target.ry - current.ry) * 0.12;
        card.style.transform = `perspective(1000px) rotateX(${current.rx}deg) rotateY(${current.ry}deg) translate3d(0,0,0)`;
        if(Math.abs(target.rx - current.rx) > 0.05 || Math.abs(target.ry - current.ry) > 0.05) raf3 = requestAnimationFrame(tick);
        else raf3 = null;
      };
      card.addEventListener('mousemove', (e: Event) => {
        const me = e as MouseEvent;
        const r = card.getBoundingClientRect();
        target.ry = ((me.clientX - r.left) / r.width - 0.5) * 8;
        target.rx = (0.5 - (me.clientY - r.top) / r.height) * 8;
        if(!raf3) raf3 = requestAnimationFrame(tick);
      });
      card.addEventListener('mouseleave', () => {
        target = {rx: 0, ry: 0}; if(!raf3) raf3 = requestAnimationFrame(tick);
      });
    });

    /* Mouse-reactive hero floating cards */
    const floats = $$('.hero .float');
    const hero = page.querySelector<HTMLElement>('.hero');
    if(floats.length && hero && !reduce) {
      const onHeroMove = (e: Event) => {
        const me = e as MouseEvent;
        const r = hero.getBoundingClientRect();
        const mx = ((me.clientX - r.left) / r.width - 0.5) * 2;
        const my = ((me.clientY - r.top) / r.height - 0.5) * 2;
        floats.forEach((el, i) => {
          const k = (i + 1) * 6;
          el.style.translate = `${mx * k}px ${my * k}px`;
        });
      };
      hero.addEventListener('mousemove', onHeroMove);
      this.cleanups.push(() => hero.removeEventListener('mousemove', onHeroMove));
    }

    /* Flow section sticky stepper */
    const flowSection = page.querySelector<HTMLElement>('#vsc-flow');
    const flowStage = page.querySelector<HTMLElement>('#vsc-flowStage');
    if(flowSection && flowStage) {
      const rail = $$('.rail-list li', flowStage);
      const stepCount = Math.max(1, Math.min(rail.length, this.flowSteps.length, this.stepImages.length));
      const thread = flowStage.querySelector<SVGLineElement>('.thread-fill');
      const threadLen = 600;
      let activeStep = -1;
      const setStep = (n: number) => {
        const next = Math.max(0, Math.min(stepCount - 1, n));
        if(next === activeStep) return;
        activeStep = next;
        this.setActiveStep(next);
        if(thread) thread.style.strokeDashoffset = String(threadLen * (1 - (next + 1) / stepCount));
      };
      setStep(0);

      // Absolute tops captured at init (scrollTop === 0 at this point)
      const sectionAbsTop = flowSection.getBoundingClientRect().top;
      const flowVisual = flowStage.querySelector<HTMLElement>('.flow-visual');
      const visualAbsTop = flowVisual
        ? flowVisual.getBoundingClientRect().top
        : flowStage.getBoundingClientRect().top;
      const isMobile = () => page.clientWidth <= 1100;

      let flowRaf: number | null = null;
      const updateFlowStep = () => {
        if(isMobile()) {
          // Mobile: start when .flow-visual top is 10% into the viewport
          const scrolled = page.scrollTop - visualAbsTop + vh() * 0.9;
          if(scrolled <= 0) {
            setStep(0);
            flowRaf = null;
            return;
          }
          setStep(Math.min(stepCount - 1, Math.floor(scrolled / 250)));
        } else {
          // Desktop: sticky section — scroll space = section height - viewport
          const scrollInSection = page.scrollTop - sectionAbsTop;
          const scrollSpace = flowSection.offsetHeight - vh();
          if(scrollSpace <= 0 || scrollInSection <= 0) {
            setStep(0);
            flowRaf = null;
            return;
          }
          setStep(Math.min(stepCount - 1, Math.floor((scrollInSection / scrollSpace) * stepCount)));
        }
        flowRaf = null;
      };
      const onFlowScroll = () => {
        if(flowRaf === null) {
          flowRaf = requestAnimationFrame(updateFlowStep);
        }
      };
      page.addEventListener('scroll', onFlowScroll, {passive: true});
      onFlowScroll();
      this.cleanups.push(() => {
        page.removeEventListener('scroll', onFlowScroll);
        if(flowRaf !== null) cancelAnimationFrame(flowRaf);
      });
    }

    /* Live feed time updates */
    const feedTimer = setInterval(() => {
      $$('.feed .t').forEach(t => {
        if(/^\d+s$/.test(t.textContent || '')) t.textContent = (parseInt(t.textContent || '0') + 1) + 's';
      });
    }, 3000);
    this.cleanups.push(() => clearInterval(feedTimer));
  }
}
